import {
  applyActionCode,
  confirmPasswordReset,
  signInWithEmailAndPassword,
  signOut,
  verifyPasswordResetCode,
} from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { COMPNAY_ROLES, isDeliver, ROLE } from '../helpers/roles'
import {
  collectionUsers,
  getCustomer,
  getDeliver,
  getManager,
  getModerator,
  getOwner,
} from '../firestoreWrappers/firestoreWrappers'
import { auth } from '../firebaseCore'
import { mapToModel } from '../models/ManagerModel'
import router from '../router'
import { useSuppliersSubsidyStore } from './suppliersSubsidy'
import { useStatisticsStore } from './statistics'
import store from '.'
import { useNewsStore } from '~/store/newsStore'
import { useMissingCompaniesStore } from './missingCompanies'
import { useCompaniesStore } from './companies'
import { useMessagesStore } from './messages'
import { useReportBugOrSuggestionStore } from './reportBugOrSuggestion'
import { useCouponsStore } from './coupons'
import { useInvoicesStore } from './invoices'
import { useManagerRoutesStore } from './managerRoutes'
import { useOrdersStatisticStore } from './ordersStatistic'
import { usePromoCodeStatisticsStore } from './promoCodeStatistics'
import { useOnlineTransactionReportsStore } from './onlineTransactionReports'
import { useRouteStore } from './routes'
import { useCompanyStore } from './company'
import { useEmployeesStore } from './employees'
import { useMenusStore } from './menus'
import { useChangeLogsStore } from './changeLogsStore'
import { useDocumentsToSignStore } from './documentToSign'
import { useSuppliersStore } from './suppliers'
import { useDeliversLocationStore } from './deliversLocation'
import { defineStore } from 'pinia'
import { ref } from 'vue'

function getRoute(role: string) {
  switch (role) {
    case ROLE.GET_DELIVER:
      return '/routes'
    case ROLE.DELIVER:
      return '/my-documents'
    case ROLE.OWNER:
      return '/company'
    case ROLE.MANAGER:
      return '/delivers-sells'
    case ROLE.MODERATOR:
      return '/moderator'
    default:
      return '/sign-out'
  }
}

function getRouteUser(role: string) {
  switch (role) {
    case ROLE.MANAGER:
      return '/subsidyBudget'
    default:
      return '/sign-out'
  }
}

const getUser = (uid: string) => getDoc(doc(collectionUsers, uid))

export const useAppStore = defineStore('app', () => {
  const user = ref<any | null>(null)
  const userData = ref<any | null>(null)
  const error = ref<any | null>(null)
  const loading = ref<boolean>(false)

  const resetState = () => {
    user.value = null
    userData.value = null
    error.value = null
    loading.value = false
  }

  const updateUserData = (newUserData: any) => {
    userData.value = newUserData
  }

  const signOutUser = () => {
    const onSuccess = () => {
      resetState()

      useChangeLogsStore().resetState()
      useMenusStore().resetState()
      useEmployeesStore().resetState()
      useCompanyStore().resetState()
      useReportBugOrSuggestionStore().resetState()
      useMessagesStore().resetState()
      useCouponsStore().resetState()
      useInvoicesStore().resetState()
      useCompaniesStore().resetState()
      useOrdersStatisticStore().resetState()
      useManagerRoutesStore().resetState()
      usePromoCodeStatisticsStore().resetState()
      useMissingCompaniesStore().resetState()
      useNewsStore().resetState()
      useSuppliersSubsidyStore().resetState()
      useStatisticsStore().resetState()
      useOnlineTransactionReportsStore().resetState()
      useRouteStore().resetState()
      useDocumentsToSignStore().resetState()
      useSuppliersStore().resetState()
      useDeliversLocationStore().resetState()

      if (store && store.dispatch) {
        store.dispatch('shared/resetState')
        store.dispatch('orders/resetState')
      }
    }
    signOut(auth).then(onSuccess)
  }

  const logIn = ({ user: loginUser, currentUserData: loginUserData }: { user: any, currentUserData: any }) => {
    const mappedUserData = mapToModel(loginUserData, loginUser.displayName)

    if (!loginUserData.data()?.isActive) {
      router.push('error-active-account')
      signOutUser()
      return
    }

    // @ts-expect-error
    if (mappedUserData.company) {
      const onCompanyFetch = (companyResponse: any) => {
        const companyStore = useCompanyStore()
        companyStore.setCompany(companyResponse)
        user.value = loginUser
        userData.value = mappedUserData
      }

      // @ts-expect-error
      getDoc(mappedUserData.company).then(onCompanyFetch)
    }
    else {
      user.value = loginUser
      userData.value = mappedUserData
    }
  }

  const checkUserData = (userParam: any) => {
    if (COMPNAY_ROLES.includes(userParam?.displayName)) {
      const handleLogIn = (userDataParam: any, currentUserData: any) => {
        if (currentUserData.data()?.isActive) {
          logIn({
            user: userDataParam,
            currentUserData,
          })
        }
        else {
          router.push('error-active-account')
          signOutUser()
        }
      }

      const handleDeliver = (authData: any) => (deliverData: any) => {
        if (!deliverData.exists() || isDeliver(deliverData.data().role)) {
          router.push('error-deliver')
          signOutUser()
        }
        else {
          handleLogIn(authData, deliverData)
        }
      }

      const handleUserLogin = (authData: any) => (deliverData: any) => handleLogIn(authData, deliverData)

      let request
      switch (userParam.displayName) {
        case ROLE.OWNER:
          request = getOwner(userParam.uid)
          break
        case ROLE.GET_DELIVER:
          request = getDeliver(userParam.uid)
          break
        case ROLE.DELIVER:
          request = getDeliver(userParam.uid)
          break
        case ROLE.MANAGER:
          request = getManager(userParam.uid)
          break
        case ROLE.MODERATOR:
          request = getModerator(userParam.uid)
          break
        case ROLE.CUSTOMER:
          request = getCustomer(userParam.uid)
          break
        default:
          signOutUser()
          break
      }

      const handleUserData = (userDataResponse: any) => {
        if (!userParam?.displayName) {
          handleDeliver(userParam)(userDataResponse)
        }
        else {
          handleUserLogin(userParam)(userDataResponse)
        }
      }

      request?.then(handleUserData).catch(() => {
        router.push('error-active-account')
        signOutUser()
      })
    }
    else {
      signOutUser()
    }
  }

  const loginWithPassword = (email: string, password: string) => {
    error.value = null
    loading.value = true

    const onLoginSuccess = ({ user: authUser }: { user: any }) => {
      loading.value = false

      if (authUser.displayName === 'customer') {
        getUser(authUser.uid).then(userResponse =>
          router.push(getRouteUser(userResponse.data()?.role)),
        )
      }
      else {
        router.push(getRoute(authUser.displayName))
      }
    }

    const onLoginError = (loginError: unknown) => {
      console.error(loginError)
      loading.value = false
      error.value = loginError
    }

    signInWithEmailAndPassword(auth, email, password)
      .then(onLoginSuccess)
      .catch(onLoginError)
  }

  const resetPassword = (code: string, password: string) => {
    error.value = null
    loading.value = true

    const onResetSuccess = () => {
      loading.value = false
    }

    const onResetError = (resetError: unknown) => {
      console.error(resetError)
      loading.value = false
      error.value = resetError
    }

    const onCodeValid = () => {
      confirmPasswordReset(auth, code, password)
        .then(onResetSuccess)
        .catch(onResetError)
    }

    verifyPasswordResetCode(auth, code).then(onCodeValid).catch(onResetError)
  }

  const verifyEmail = (code: string) => {
    error.value = null
    loading.value = true

    const onVerifySuccess = () => {
      loading.value = false
    }

    const onVerifyError = (verifyError: unknown) => {
      console.error(verifyError)
      loading.value = false
      error.value = verifyError
    }

    applyActionCode(auth, code)
      .then(onVerifySuccess)
      .catch(onVerifyError)
  }

  return {
    user,
    userData,
    error,
    loading,

    resetState,
    updateUserData,
    signOutUser,
    checkUserData,
    logIn,
    loginWithPassword,
    resetPassword,
    verifyEmail,
  }
})
